/**
 * Tonaufnahmen auf der Flaeche.
 *
 * Ablage
 *   Die Datei wandert als Data-URL in die Datenbank, wie schon die Bilder.
 *   Damit liegt sie auf dem Geraet und laesst sich ohne Netz abspielen – es
 *   gibt in dieser App keinen Server, auf den man sie legen koennte.
 *
 * Wellenform
 *   Wird beim Einfuegen EINMAL berechnet und am Objekt gespeichert. Das
 *   Dekodieren einer Viertelstunde Audio dauert spuerbar; das bei jedem
 *   Oeffnen der Flaeche zu wiederholen waere Verschwendung. Klappt das
 *   Dekodieren nicht (seltenes Format, kein AudioContext), fehlt einfach die
 *   Wellenform – abspielen laesst sich die Datei trotzdem, denn das macht
 *   das <audio>-Element selbst.
 */

/** Wie viele Balken die Wellenform hat. Genug fuers Auge, wenig fuer die DB. */
const PEAK_COUNT = 96;

/** Formate, die iPadOS zuverlaessig abspielt. */
export const AUDIO_ACCEPT = 'audio/*,.mp3,.m4a,.wav,.aif,.aiff,.caf,.aac,.flac';

export interface AudioInfo {
  /** Die Datei als Data-URL. */
  src: string;
  name: string;
  /** Laenge in Sekunden; 0 wenn unbekannt. */
  duration: number;
  /** Ausschlaege 0..1. Leer, wenn das Dekodieren nicht ging. */
  peaks: number[];
}

/**
 * Liest eine Datei als Data-URL.
 * Exportiert, weil auch das Artwork der Veroeffentlichungen (ui/artwork.ts)
 * und der Audio-Player der Lead Sheets (ui/audio-panel.ts) genau das
 * brauchen – eine Stelle statt drei fast identische FileReader-Aufrufe.
 */
export function readDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(String(reader.result ?? '')));
    reader.addEventListener('error', () => reject(reader.error));
    reader.readAsDataURL(file);
  });
}

/**
 * Rechnet die Rohdaten auf PEAK_COUNT Balken herunter.
 * Je Balken der groesste Betrag – das sieht aus wie eine Wellenform und ist
 * billiger als ein Mittelwert ueber alles.
 */
function peaksFrom(buffer: AudioBuffer): number[] {
  const channel = buffer.getChannelData(0);
  const perBar = Math.max(1, Math.floor(channel.length / PEAK_COUNT));
  const peaks: number[] = [];
  let loudest = 0;

  for (let i = 0; i < PEAK_COUNT; i += 1) {
    const start = i * perBar;
    let max = 0;
    // Nicht jeden Wert ansehen: bei 44,1 kHz reicht jeder 16. voellig.
    for (let j = start; j < start + perBar && j < channel.length; j += 16) {
      const value = Math.abs(channel[j] as number);
      if (value > max) max = value;
    }
    if (max > loudest) loudest = max;
    peaks.push(max);
  }

  // Auf die lauteste Stelle normieren, sonst sieht eine leise Aufnahme
  // aus wie ein flacher Strich.
  if (loudest > 0) return peaks.map((p) => Math.min(1, p / loudest));
  return peaks;
}

/** Laenge aus dem <audio>-Element, wenn das Dekodieren nicht geklappt hat. */
function durationFrom(src: string): Promise<number> {
  return new Promise((resolve) => {
    const el = new Audio();
    const done = (value: number) => resolve(Number.isFinite(value) ? value : 0);
    el.addEventListener('loadedmetadata', () => done(el.duration));
    el.addEventListener('error', () => done(0));
    el.preload = 'metadata';
    el.src = src;
    // Nicht ewig warten, falls das Format nicht gelesen werden kann.
    setTimeout(() => done(0), 4000);
  });
}

/** Liest eine Tondatei ein: Data-URL, Laenge und Wellenform. */
export async function readAudio(file: File): Promise<AudioInfo> {
  const src = await readDataUrl(file);
  const name = file.name || 'Aufnahme';

  const Ctx: typeof AudioContext | undefined =
    window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!Ctx) return { src, name, duration: await durationFrom(src), peaks: [] };

  let ctx: AudioContext | null = null;
  try {
    ctx = new Ctx();
    const buffer = await ctx.decodeAudioData(await file.arrayBuffer());
    return { src, name, duration: buffer.duration, peaks: peaksFrom(buffer) };
  } catch {
    // Format, das der Browser nicht dekodieren kann. Abspielen geht oft
    // trotzdem, also nur ohne Wellenform weitermachen.
    return { src, name, duration: await durationFrom(src), peaks: [] };
  } finally {
    // Ein offener AudioContext haelt sonst Rechenzeit fest.
    void ctx?.close().catch(() => undefined);
  }
}

/** "3:07" – oder "–:––", solange die Laenge unbekannt ist. */
export function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '–:––';
  const total = Math.round(seconds);
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Zeichnet die Wellenform als SVG.
 * `progress` (0..1) faerbt den bereits gespielten Teil ein.
 */
export function waveformSvg(peaks: number[]): SVGSVGElement {
  const NS = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('class', 'obj-audio__wave');
  svg.setAttribute('viewBox', `0 0 ${PEAK_COUNT * 3} 40`);
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('aria-hidden', 'true');

  const bars = peaks.length > 0 ? peaks : new Array(PEAK_COUNT).fill(0.08);
  bars.forEach((peak, i) => {
    // Mindesthoehe, damit auch Stille als feine Linie sichtbar bleibt.
    const height = Math.max(2, peak * 36);
    const bar = document.createElementNS(NS, 'rect');
    bar.setAttribute('x', String(i * 3));
    bar.setAttribute('y', String((40 - height) / 2));
    bar.setAttribute('width', '2');
    bar.setAttribute('height', String(height));
    bar.setAttribute('rx', '1');
    bar.setAttribute('class', 'obj-audio__bar');
    bar.dataset.index = String(i);
    svg.append(bar);
  });
  return svg;
}

/** Faerbt die Balken bis zur gespielten Stelle ein. */
export function paintProgress(svg: SVGSVGElement, progress: number): void {
  const bars = svg.querySelectorAll<SVGRectElement>('.obj-audio__bar');
  const upTo = Math.round(progress * bars.length);
  bars.forEach((bar, i) => {
    bar.classList.toggle('obj-audio__bar--played', i < upTo);
  });
}

/** Anteil 0..1 aus einem Klick auf die Wellenform. */
export function seekRatio(svg: SVGSVGElement, clientX: number): number {
  const rect = svg.getBoundingClientRect();
  if (rect.width === 0) return 0;
  return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
}
